import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Mail,
  Phone,
  Lock,
  CheckCircle,
  AlertCircle,
  Home,
  Building2,
  Eye,
  EyeOff,
  ArrowRight,
  Shield
} from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '../utils/supabaseClient';

const API_URL = import.meta.env.VITE_API_URL || 'https://homequest-api-1.onrender.com';

export default function AcceptInvitePage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [inviteData, setInviteData] = useState<any>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inviteToken = params.get('token');

    if (inviteToken) {
      setToken(inviteToken);
      fetchInviteDetails(inviteToken);
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [location]);

  // Track mouse for gradient effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const fetchInviteDetails = async (inviteToken: string) => {
    try {
      const response = await fetch(`${API_URL}/api/team-members/invite/${inviteToken}`);
      const data = await response.json();

      if (data.success) {
        setInviteData(data.data);
      } else {
        setError(data.error || 'Invalid or expired invitation');
      }
    } catch (err: any) {
      setError('Failed to load invitation details');
      console.error('Error fetching invite:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/team-members/accept-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          phoneNumber,
          password,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);

        // Automatically log in the user with their new credentials
        try {
          const { error: signInError } = await supabase.auth.signInWithPassword({
            email: inviteData.email,
            password: password,
          });

          if (signInError) {
            console.error('Auto-login failed:', signInError);
            // Fall back to manual login if auto-login fails
            setTimeout(() => {
              navigate('/login');
            }, 2000);
          } else {
            // Successfully logged in, redirect to dashboard
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
          }
        } catch (loginError) {
          console.error('Login error:', loginError);
          // Fall back to manual login
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      } else {
        setError(data.error || 'Failed to accept invitation');
      }
    } catch (err: any) {
      setError('Failed to accept invitation. Please try again.');
      console.error('Error accepting invite:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex relative overflow-hidden bg-black">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 opacity-30 transition-all duration-100"
            style={{
              background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px,
                rgba(59, 130, 246, 0.5) 0%,
                transparent 50%)`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-gray-900 to-black" />
          <div className="absolute top-20 left-20 w-96 h-96 bg-blue-600 rounded-full filter blur-3xl opacity-20 animate-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-600 rounded-full filter blur-3xl opacity-20 animate-pulse animation-delay-2000" />
        </div>

        <div className="flex items-center justify-center w-full relative z-10">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-300 text-lg">Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex relative overflow-hidden bg-black">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-gray-900 to-black" />
          <div className="absolute top-20 left-20 w-96 h-96 bg-green-600 rounded-full filter blur-3xl opacity-20 animate-pulse" />
        </div>

        <div className="flex items-center justify-center w-full p-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl blur-xl opacity-20"></div>

            <div className="relative bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-800 p-12 max-w-md w-full text-center">
              <div className="w-20 h-20 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6 border border-green-500/30">
                <CheckCircle className="w-12 h-12 text-green-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">Welcome to the Team!</h2>
              <p className="text-gray-300 mb-2">
                Your account has been activated successfully.
              </p>
              <p className="text-gray-400 text-sm mb-6">
                You can now start collaborating with your team.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span>Redirecting to dashboard...</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (error && !inviteData) {
    return (
      <div className="min-h-screen flex relative overflow-hidden bg-black">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-gray-900 to-black" />
          <div className="absolute top-20 left-20 w-96 h-96 bg-red-600 rounded-full filter blur-3xl opacity-20 animate-pulse" />
        </div>

        <div className="flex items-center justify-center w-full p-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-orange-600 rounded-3xl blur-xl opacity-20"></div>

            <div className="relative bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-800 p-12 max-w-md w-full text-center">
              <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-3">Invalid Invitation</h2>
              <p className="text-gray-300 mb-8">{error}</p>
              <button
                onClick={() => navigate('/')}
                className="relative w-full group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur group-hover:blur-md transition-all duration-300"></div>
                <div className="relative w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold text-white flex items-center justify-center gap-2 group-hover:shadow-xl transition-all duration-300">
                  <span>Go to Home</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-black">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 opacity-30 transition-all duration-100"
          style={{
            background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px,
              rgba(59, 130, 246, 0.5) 0%,
              transparent 50%)`
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-gray-900 to-black" />

        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />

        <div className="absolute top-20 left-20 w-96 h-96 bg-blue-600 rounded-full filter blur-3xl opacity-20 animate-pulse" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-600 rounded-full filter blur-3xl opacity-20 animate-pulse animation-delay-2000" />
      </div>

      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative z-10 items-center justify-center p-12">
        <div className="max-w-lg">
          <div className="flex items-center gap-4 mb-12">
            <div className="relative">
              <div className="absolute inset-0 bg-blue-600 blur-xl opacity-50"></div>
              <div className="relative bg-gradient-to-br from-blue-500 to-blue-700 p-4 rounded-2xl shadow-2xl">
                <Home className="w-10 h-10 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white">HomeQuest</h1>
              <p className="text-blue-400 text-sm tracking-widest">ENTERPRISE</p>
            </div>
          </div>

          <h2 className="text-5xl font-bold text-white mb-6 leading-tight">
            Join Your Team
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-600">
              Start Collaborating
            </span>
          </h2>

          <p className="text-gray-400 text-lg mb-12">
            Complete your profile to access your team's projects and start managing construction workflows together.
          </p>

          <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Building2 className="w-6 h-6 text-blue-400" />
              <div>
                <div className="text-sm text-gray-400">Joining Team</div>
                <div className="text-lg font-semibold text-white">{inviteData?.department}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-6 h-6 text-blue-400" />
              <div>
                <div className="text-sm text-gray-400">Your Email</div>
                <div className="text-lg font-semibold text-white">{inviteData?.email}</div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-6 text-gray-500">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm">Enterprise Security</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <div className="inline-flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-3 rounded-xl">
                <Home className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white">HomeQuest</h1>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl blur-xl opacity-20"></div>

            <div className="relative bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-800 p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">
                  Complete Your Profile
                </h2>
                <p className="text-gray-400">
                  You've been invited as <span className="text-blue-400 font-semibold">{inviteData?.role}</span>
                </p>
              </div>

              {/* Invitation Details */}
              <div className="bg-gray-800/50 rounded-xl p-4 mb-6 space-y-3">
                <div className="text-gray-300">
                  <p className="text-sm text-gray-400 mb-1">Full Name</p>
                  <p className="font-medium">{inviteData?.fullName}</p>
                </div>
                <div className="text-gray-300">
                  <p className="text-sm text-gray-400 mb-1">Department</p>
                  <p className="font-medium">{inviteData?.department}</p>
                </div>
              </div>

              {error && (
                <div className="mb-6 p-3 bg-red-900/20 border border-red-800 rounded-xl">
                  <p className="text-sm text-red-400 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {error}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-500"
                      placeholder="+1 (555) 123-4567"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Create Password *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-11 pr-12 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-500"
                      placeholder="Min. 8 characters"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-11 pr-12 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-500"
                      placeholder="Re-enter password"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="relative w-full group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur group-hover:blur-md transition-all duration-300"></div>
                  <div className="relative w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold text-white flex items-center justify-center gap-2 group-hover:shadow-xl transition-all duration-300">
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Activating Account...</span>
                      </>
                    ) : (
                      <>
                        <span>Accept Invitation</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </div>
                </button>

                <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                  <Shield className="w-4 h-4" />
                  <span>256-bit SSL encryption • SOC 2 Type II certified</span>
                </div>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-800">
                <p className="text-gray-400 text-sm text-center">
                  Need help? Contact your team administrator
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-xs text-gray-500">
            <p>© 2025 HomeQuest Tech™. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
